<template>
  <div v-if="enabled" :class="['chat-img-composer', { 'chat-img-composer--dragging': dragging }]">
    <input ref="fileInput"
           type="file"
           accept="image/*"
           multiple
           class="chat-img-composer__input"
           tabindex="-1"
           aria-hidden="true"
           @change="onPicked">

    <p v-if="dragging" class="chat-img-composer__drop">Drop images to attach</p>

    <div v-if="modelValue.length" class="chat-img-composer__strip">
      <div v-for="item in modelValue" :key="item.id" class="chat-img-composer__thumb">
        <img :src="item.url" :alt="item.file.name">
        <button type="button"
                class="chat-img-composer__remove"
                title="Remove image"
                :disabled="disabled"
                @click="remove(item)">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>

    <p v-if="error" class="chat-img-composer__error" role="alert">{{ error }}</p>
  </div>
</template>

<script>
  import { validateImages, newClientId } from '../../helpers/chatImages.js';

  // Staging area for chat images. The parent owns the list via v-model (items are
  // { id, file, url }) and places its own attach button, calling openPicker(); it also
  // forwards paste / drag / drop events from its composer area to onPaste / onDragOver /
  // onDragLeave / onDrop. Everything is a no-op while `enabled` is false.
  export default {
    name: 'ChatImageComposer',
    props: {
      modelValue: { type: Array, default: () => [] },
      enabled: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
      return {
        error: '',
        dragging: false
      };
    },
    watch: {
      // Revoke object URLs for anything that left the list (removed, sent, or cleared).
      modelValue(next, prev) {
        const keep = new Set((next || []).map(i => i.url));
        (prev || []).forEach(i => { if (!keep.has(i.url)) URL.revokeObjectURL(i.url); });
        if (!next || !next.length) this.error = '';
      }
    },
    beforeUnmount() {
      (this.modelValue || []).forEach(i => URL.revokeObjectURL(i.url));
    },
    methods: {
      openPicker() {
        if (!this.enabled || this.disabled) return;
        this.$refs.fileInput?.click();
      },

      onPicked(evt) {
        this.addFiles(Array.from(evt.target.files || []));
        evt.target.value = ''; // allow picking the same file again
      },

      addFiles(files) {
        if (!this.enabled || this.disabled || !files.length) return;
        const { accepted, errors } = validateImages(files, this.modelValue.length);
        this.error = errors.join(' ');
        if (!accepted.length) return;
        const items = accepted.map(file => ({ id: newClientId(), file, url: URL.createObjectURL(file) }));
        this.$emit('update:modelValue', [...this.modelValue, ...items]);
      },

      remove(item) {
        this.error = '';
        this.$emit('update:modelValue', this.modelValue.filter(i => i !== item));
      },

      // Only swallows the paste when it actually carried an image, so text paste is untouched.
      onPaste(evt) {
        if (!this.enabled || this.disabled) return;
        const items = Array.from(evt.clipboardData?.items || []);
        const files = items
          .filter(i => i.kind === 'file' && (i.type || '').startsWith('image/'))
          .map(i => i.getAsFile())
          .filter(Boolean)
          .map((f, idx) => {
            const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            return new File([f], `pasted-${Date.now()}-${idx}.${ext}`, { type: f.type });
          });
        if (!files.length) return;
        evt.preventDefault();
        this.addFiles(files);
      },

      onDragOver(evt) {
        if (!this.enabled || this.disabled) return;
        if (!Array.from(evt.dataTransfer?.types || []).includes('Files')) return;
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'copy';
        this.dragging = true;
      },

      onDragLeave(evt) {
        // Ignore leaves into child elements of the drop area.
        if (evt.currentTarget && evt.relatedTarget && evt.currentTarget.contains(evt.relatedTarget)) return;
        this.dragging = false;
      },

      onDrop(evt) {
        this.dragging = false;
        if (!this.enabled || this.disabled) return;
        const files = Array.from(evt.dataTransfer?.files || []);
        if (!files.length) return;
        evt.preventDefault();
        this.addFiles(files);
      }
    }
  };
</script>

<style scoped>
  .chat-img-composer__input {
    display: none;
  }

  .chat-img-composer__drop {
    margin: 0 0 6px;
    padding: 10px;
    border: 2px dashed var(--gs-blue, #2f6fed);
    border-radius: 9px;
    text-align: center;
    font-size: 1.2rem;
    color: var(--gs-blue, #2f6fed);
  }

  .chat-img-composer__strip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
  }

  .chat-img-composer__thumb {
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--gs-border, #e7ebf3);
    background: #f2f5fa;
  }

  .chat-img-composer__thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .chat-img-composer__remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(17, 40, 80, .7);
    color: #fff;
    font-size: 1.1rem;
    line-height: 20px;
    cursor: pointer;
  }

  .chat-img-composer__remove:disabled {
    cursor: not-allowed;
    opacity: .5;
  }

  .chat-img-composer__error {
    margin: 0 0 6px;
    font-size: 1.15rem;
    color: #c62828;
  }
</style>
